import $ from 'jQuery';
import immediateApi from '../api/immediateApi';

/**
 * Retrieves the positions from the immediate api and maps them to the axis id.
 * If a drive is not represented in the configuration, the result is discarded.
 * @param {Config} pConfig
 * @returns {Promise<{ [axisId: string]: number }>}
 */
export function getAxisIdToPositionMapForContoller(pConfig) {
  return immediateApi.getAllPositionsForController(pConfig.getController().getUUID())
    .then(function(pPositionsMap) {
      const lRetval = {};

      Object.keys(pPositionsMap).forEach(function(pDriveLetter) {
        const lAxisNumber = getDriveNumberFromDriveLetter(pDriveLetter);
        if (lAxisNumber <= 0) {
          return;
        }

        const lAxis = pConfig.listAxes().find(function(pAxis) { return pAxis.getDriveNumber() === lAxisNumber; });
        if (lAxis) {
          lRetval[lAxis.getUUID()] = pPositionsMap[pDriveLetter];
        }
      });

      return lRetval;
    });
}

/**
 * Aggregates the positions form getAxisIdToPositionMapForContoller across all controllers
 * @param {Library} pLibrary
 * @returns {Promise<{ [axisId: string]: number }>}
 */
export function getAxisIdToPositionMapForLibrary(pLibrary) {
  const lConfigurations = pLibrary.getConfigManager().getConfigurationList();
  return Promise.all(lConfigurations.map(getAxisIdToPositionMapForContoller)).then(function(pMapList) {
    return Object.assign({}, ...pMapList);
  });
}

/**
 * Returns the drive number [1-4] from the drive letter. Return -1 if not found.
 * @param {string} pAxisLetter
 * @returns {number}
 */
export function getDriveNumberFromDriveLetter(pAxisLetter) {
  const lDriveNumber = ["x", "y", "z", "w"].indexOf(pAxisLetter.toLowerCase());
  return lDriveNumber != -1 ? lDriveNumber + 1 : -1;
}

/**
 * Sends a 'stop' to all the controllers
 * @param machineLogicLibrary
 * @return {Promise<Awaited<unknown>[]>}
 */
export function stopAll(machineLogicLibrary) {
  const lStops = machineLogicLibrary
    .getConfigManager()
    .getConfigurationList()
    .map(pConfig => immediateApi.stop(pConfig.getController().getUUID()));

  return Promise.all(lStops);
}

/**
 * Downloads a json
 * @param {string} pFileName
 * @param pContent
 */
export function downloadJson(pFilename, pContent) {
  // Temporary <a> tag that clicks automatically and removes itself
  $("<a />",
    {
      "download": pFilename,
      "href" : "data:application/json," + encodeURIComponent(pContent)
    })
    .on('click', (e) => {
      e.stopPropagation();
      $(this).remove();
    })[0].click();
}

/**
 * A data object to describe an io's physical connection.
 * @typedef {Object} Io
 * @property {string} uuid - The uuid of the io.
 * @property {string} controllerId - The id of the controller that runs the io.
 * @property {number} device - The id of the device that runs the io.
 * @property {number} pinNumber - The id of the pin that the io is connected to.
 */
/**
 * Get ios matching a list of uuids from the config manager Inputs or Outputs.
 * @param {string[]} pIoUuids
 * @param {(Input | Output)[]} pConfigManagerIos
 * @return {Io[]}
 */
export function getIosFromUuids(pIoUuids, pConfigManagerIos) {
  const lConfigManagerIosMap = {};
  for (const lConfigManagerIo of pConfigManagerIos) {
    lConfigManagerIosMap[lConfigManagerIo.getUUID()] = lConfigManagerIo;
  }

  const lIos = [];
  for (const lIoUuid of pIoUuids) {
    const lIo = lConfigManagerIosMap[lIoUuid];
    if (!lIo) {
      console.error(`Unable to find io with uuid=${lIoUuid}`);

      continue;
    }

    lIos.push({
      uuid: lIoUuid,
      controllerId: lIo.getControllerId(),
      device: lIo.getDevice(),
      pinNumber: lIo.getPin(),
    });
  }

  return lIos;
}

export function versionToJson(pVersion) {
  return {
    major: pVersion.major,
    minor: pVersion.minor
  };
}
